/**
 * Lead creation — the ONE shared "+ New Lead" path (2026-07-03, parity
 * audit GAP 1).
 *
 * Extracted VERBATIM from the Existing screen's `ingest` (Leads.tsx) so the
 * List/Board lens toolbars create leads through the IDENTICAL pipeline —
 * contact-spine match (AD-039: auto-link ONLY on an exact phone/email match,
 * weaker scores ride as staff-confirm candidates) → pure `makeLead` →
 * `createLead` (Firestore mints the real id) → best-effort reverse
 * Contact→Lead back-ref. One create path; no lens-local variant can drift.
 *
 * Impure by nature (persists); the pure builders it composes (`makeLead`,
 * `matchContacts`, `bestAutoLink`) stay individually unit-tested.
 */
import {
  leadId,
  makeLead,
  type Lead,
  type LeadInboundDraft,
} from './leads';
import { createLead } from './leadStore';
import { appendLeadRef } from './projectStore';
import {
  bestAutoLink,
  matchContacts,
  type ContactLite,
} from './contactMatch';

/** The blank manual-entry draft (walk-in / phone-call lead). Moved here from
 *  Leads.tsx so every "+ New Lead" button seeds the identical record. */
export function BLANK_MANUAL(): LeadInboundDraft {
  return {
    source: 'manual_referral',
    name: 'New manual lead',
    phone: '',
    email: '',
    location: '',
    concern: '',
    propertyType: '',
    ownerName: 'Khilendra',
    notes: 'Manually entered (Khilendra / Rohit).',
  };
}

/**
 * Create a lead from an inbound draft through the one shared pipeline.
 * `contactPool` = the live `clients/` masters (the AD-039 match source);
 * `placeholderSeq` seeds the pure LD-#### placeholder id (Firestore's minted
 * id replaces it). Returns the REAL Firestore id so the caller can select /
 * open the new lead.
 */
export async function createInboundLead(
  draft: LeadInboundDraft,
  contactPool: ContactLite[],
  placeholderSeq: number,
  actorName: string,
  nowISO: string = new Date().toISOString(),
): Promise<string> {
  // Contact spine (AD-039) — match the enquiry against the live `clients`
  // snapshot BEFORE creating the lead. An exact phone/email match auto-links
  // (stamps contactId); weaker scores ride in matchCandidates for the
  // staff-confirm chip. Owner-locked: auto-link ONLY on exact phone/email.
  const results = matchContacts(
    { name: draft.name, phone: draft.phone, email: draft.email },
    contactPool,
  );
  const auto = bestAutoLink(results);
  const draftLead: Lead = makeLead(
    leadId(placeholderSeq),
    {
      ...draft,
      contactId: auto?.contactId ?? null,
      matchCandidates: results
        .slice(0, 3)
        .map(({ contactId, clientNo, name, score }) => ({
          contactId,
          clientNo,
          name,
          score,
        })),
    },
    nowISO,
  );
  const id = await createLead(draftLead, actorName);
  // On an auto-link, maintain the reverse Contact→Lead back-ref (AD-039 §8).
  if (auto) {
    try {
      await appendLeadRef(auto.contactId, id, actorName);
    } catch {
      /* reverse link is convenience; lead creation already succeeded */
    }
  }
  return id;
}
